import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from './locales/en.json';
import fr from './locales/fr.json';
import es from './locales/es.json';
import pt from './locales/pt.json';
import nl from './locales/nl.json';
import de from './locales/de.json';

export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English',   flag: '🇬🇧' },
  { code: 'fr', label: 'Français',  flag: '🇫🇷' },
  { code: 'es', label: 'Español',   flag: '🇪🇸' },
  { code: 'pt', label: 'Português', flag: '🇵🇹' },
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
  { code: 'de', label: 'Deutsch',    flag: '🇩🇪' },
];

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      fr: { translation: fr },
      es: { translation: es },
      pt: { translation: pt },
      nl: { translation: nl },
      de: { translation: de },
    },
    fallbackLng: ['fr', 'en'],
    supportedLngs: ['en', 'fr', 'es', 'pt', 'nl', 'de'],
    // Browser detection respects the user preference saved to localStorage
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'tofbox_lang',
      caches: ['localStorage'],
    },
    interpolation: { escapeValue: false },
    returnEmptyString: false,
  });

// <html lang> follows the language shown (screen readers, search engines).
document.documentElement.lang = i18n.resolvedLanguage || 'en';
i18n.on('languageChanged', (lng) => { document.documentElement.lang = i18n.resolvedLanguage || lng; });

/** Sync the user's saved language (from their profile) with i18n on login. */
export const syncUserLanguage = (lang) => {
  if (!lang) return;
  if (i18n.language !== lang) i18n.changeLanguage(lang);
};

/**
 * IP-based language auto-detection for first-time visitors.
 *
 * Order of precedence remains:
 *   1. Explicit user choice (localStorage: tofbox_lang)  ← from LanguageSelector
 *   2. IP geolocation (this function, backend /api/geo/detect-language)
 *   3. navigator.language / fallbackLng
 *
 * Guarantees: this call is best-effort. It never throws, never blocks the UI,
 * and never overrides a language the user picked themselves. If the backend
 * is down or the IP is private, we silently keep the current i18n language.
 */
export const detectLanguageFromIP = async () => {
  try {
    // If the user has an explicit preference, don't touch it.
    if (typeof window !== 'undefined' && window.localStorage.getItem('tofbox_lang')) {
      return null;
    }
    const base = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_BACKEND_URL) || '';
    if (!base) return null;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 3500);
    const res = await fetch(`${base}/api/geo/detect-language`, {
      method: 'GET',
      signal: controller.signal,
      credentials: 'omit', // public endpoint — don't leak auth cookies
    });
    clearTimeout(timer);
    if (!res.ok) return null;
    const data = await res.json();
    const lang = data && data.language;
    if (!lang || !['en', 'fr', 'es', 'pt', 'nl', 'de'].includes(lang)) return null;
    if (data.source !== 'ip') return null; // only trust ip-derived results
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
    return lang;
  } catch (_e) {
    return null;
  }
};

export default i18n;
